import React, { useState } from 'react';
import { 
  Button, 
  Badge, 
  Popover, 
  OverlayTrigger, 
  ListGroup,
  Dropdown
} from 'react-bootstrap';
import { 
  FaBell, 
  FaCheckCircle, 
  FaExclamationTriangle, 
  FaInfoCircle, 
  FaTimesCircle, 
  FaCheck,
  FaTrash
} from 'react-icons/fa';
import { useNotifications, NOTIFICATION_TYPES } from '../../context/NotificationContext';

// Format timestamp to a readable format
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000 / 60); // difference in minutes
  
  if (diff < 1) return 'Just now';
  if (diff < 60) return `${diff} minute${diff === 1 ? '' : 's'} ago`;
  
  const hours = Math.floor(diff / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  
  // For older notifications, show the actual date
  return date.toLocaleDateString([], { 
    month: 'short', 
    day: 'numeric', 
    hour: '2-digit', 
    minute: '2-digit' 
  });
};

// Get icon for notification type
const getNotificationIcon = (type) => {
  switch (type) {
    case NOTIFICATION_TYPES.SUCCESS:
      return <FaCheckCircle className="text-success" />;
    case NOTIFICATION_TYPES.ERROR:
      return <FaTimesCircle className="text-danger" />;
    case NOTIFICATION_TYPES.WARNING:
      return <FaExclamationTriangle className="text-warning" />;
    case NOTIFICATION_TYPES.INFO:
    default:
      return <FaInfoCircle className="text-info" />;
  }
};

const NotificationBell = ({ iconButtonStyle, iconButtonHoverStyle }) => {
  const { 
    notifications, 
    getUnreadCount, 
    markAsRead, 
    markAllAsRead, 
    clearAll 
  } = useNotifications();
  const [show, setShow] = useState(false);
  
  const unreadCount = getUnreadCount();
  
  // Handle clicking a notification
  const handleNotificationClick = (id) => {
    markAsRead(id);
  };
  
  // Handle clicking mark all as read
  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };
  
  // Handle clicking clear all
  const handleClearAll = () => {
    clearAll();
  };
  
  // Notification dropdown content
  const notificationsPopover = (
    <Popover id="notifications-popover" className="notifications-popover">
      <Popover.Header className="d-flex justify-content-between align-items-center">
        <strong>Notifications</strong>
        <div>
          {unreadCount > 0 && (
            <Button 
              variant="link" 
              className="p-0 text-primary me-2" 
              onClick={handleMarkAllAsRead}
              size="sm"
            >
              <FaCheck className="me-1" size={12} />
              <small>Mark all read</small>
            </Button>
          )}
          {notifications.length > 0 && (
            <Button 
              variant="link" 
              className="p-0 text-danger" 
              onClick={handleClearAll}
              size="sm"
            >
              <FaTrash className="me-1" size={12} />
              <small>Clear all</small>
            </Button>
          )}
        </div>
      </Popover.Header>
      <Popover.Body className="p-0">
        {notifications.length === 0 ? (
          <div className="text-center py-3 text-muted">
            <p className="mb-0">No notifications</p>
          </div>
        ) : (
          <ListGroup variant="flush" style={{ maxHeight: '350px', overflowY: 'auto' }}>
            {notifications.map(notification => (
              <ListGroup.Item 
                key={notification.id}
                action
                onClick={() => handleNotificationClick(notification.id)}
                className={`border-bottom ${!notification.read ? 'bg-light' : ''}`}
              >
                <div className="d-flex align-items-start">
                  <div className="me-2 pt-1">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-center">
                      <strong className="me-auto">{notification.title}</strong>
                      <small className="text-muted">
                        {formatTimestamp(notification.timestamp)}
                      </small>
                    </div>
                    <p className="mb-0 text-break">{notification.message}</p>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Popover.Body>
    </Popover>
  );

  return (
    <OverlayTrigger
      trigger="click"
      placement="bottom"
      show={show}
      onToggle={(nextShow) => setShow(nextShow)}
      overlay={notificationsPopover}
      rootClose
    >
      <div className="position-relative d-inline-block">
        <Button 
          style={iconButtonStyle} 
          className="icon-btn"
        >
          <FaBell />
        </Button>
        {unreadCount > 0 && (
          <Badge 
            bg="danger" 
            pill 
            className="position-absolute top-0 end-0 translate-middle"
            style={{ fontSize: '0.6rem', padding: '0.2rem 0.35rem' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </Badge>
        )}
      </div>
    </OverlayTrigger>
  );
};

export default NotificationBell; 